/**
 * Feature Flags Configuration
 * 
 * Centralized configuration for disabled features/routes.
 * This is the single source of truth for navigation rendering and route guarding.
 */

/**
 * Routes/sections that are currently disabled (not ready for use).
 * These will show "Coming soon" in the UI and redirect to a placeholder page on direct access.
 * 
 * Key: sidebar item id (matches sidebarItems[].id)
 * Value: true = disabled, false = enabled
 */
export const DISABLED_ROUTES: Record<string, boolean> = {
  // All sections are live
  // Previously disabled (now enabled):
  // - chats, guru, definition-servicemap, definition-journeys, definition-storyboards
  // - design, delivery (enabled 2026-02-03)
  // - base-specs, contracts, technical-specs, evidence, releases, approvals, audit-log (enabled 2026-03-12)
};

/**
 * Check if a route/section is disabled.
 * Also handles sub-routes (e.g., "design-architecture" is disabled if "design" is disabled).
 * 
 * @param sectionId - The section ID to check (e.g., "chats", "design", "design-architecture")
 * @returns true if the section is disabled
 */
export function isRouteDisabled(sectionId: string): boolean {
  // Check exact match first
  if (DISABLED_ROUTES[sectionId]) {
    return true;
  }
  
  // Check if parent section is disabled (for sub-routes like "design-architecture")
  const parentSection = sectionId.split('-')[0];
  if (parentSection !== sectionId && DISABLED_ROUTES[parentSection]) {
    return true;
  }
  
  return false;
}

/**
 * Tooltip message shown for disabled navigation items.
 */
export const COMING_SOON_TOOLTIP = 'Coming soon';

/**
 * Get display name for a disabled section (for the Coming Soon page).
 */
export function getDisabledSectionDisplayName(sectionId: string): string {
  const displayNames: Record<string, string> = {
    chats: 'Chats',
    guru: 'Product Guru',
    design: 'Design',
    delivery: 'Delivery',
    'design-architecture': 'Product Architecture',
    'design-areas': 'Application Areas',
    'delivery-architecture': 'Technical Architecture',
    'delivery-storymap': 'Story Map',
    'delivery-mvp': 'MVP Scope',
    'delivery-stories': 'Stories',
    'definition-servicemap': 'Service Map',
    'definition-journeys': 'User Journeys',
    'definition-storyboards': 'Storyboard',
    // Enterprise features (hidden for MVP)
    'base-specs': 'Base Specs',
    'contracts': 'Contracts',
    'technical-specs': 'Technical Specs',
    'evidence': 'Evidence',
    'releases': 'Releases',
    'approvals': 'Approvals',
    'audit-log': 'Audit Log',
  };
  
  return displayNames[sectionId] || sectionId.charAt(0).toUpperCase() + sectionId.slice(1);
}
